import {
  Autocomplete,
  Button,
  DatePicker,
  IconButton,
  Checkbox as CustomCheckbox
} from '@fullscreendigital/carrefour-dex-fe'
import { Box, Checkbox, FormControlLabel, FormGroup, FormHelperText, Grid } from '@mui/material'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'
import { Input } from 'components/core-ui/Input'
import dayjs from 'dayjs'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import sl from '../../../partners.modules.scss'
import { useNotification } from 'hooks'
import {
  useCountriesQuery,
  useDelegatesQuery,
  useLegalEntityTypesQuery,
  useManagersQuery,
  useStoresQuery
} from 'components/iam/partners/api'
import { AddShoppingCart, GroupsOutlined, Person, KeyboardArrowLeft } from '@mui/icons-material'
import EditManagerModal from 'components/iam/partners/Managers/EditManager/EditManagerModal'
import { ChangeEvent, useEffect, useState } from 'react'
import EditDelegateModal from 'components/iam/partners/Delegates/EditDelegate/EditDelegateModal'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { useSelectSearch } from 'hooks/useSelectSearch'
import { NewStoreModal } from 'components/iam/partners/Stores/NewStoreModal'
import { PartnerConfirmationModal } from '../../PartnerConfirmationModal'

const EditAssignmentStep = ({
  formik,
  onBack,
  externalPartnerType,
  partnerFormik,
  isSubmitting
}) => {
  const [managersOptions, setManagersOptions] = useState([])
  const [delegatesOptions, setDelegatesOptions] = useState([])
  const [storesOptions, setStoresOptions] = useState([])
  const [countriesOptions, setCountriesOptions] = useState([])
  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false)
  const [isDelegateModalOpen, setIsDelegateModalOpen] = useState(false)
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false)
  const [isLDAPdisabled, setIsLDAPDisabled] = useState(false)
  const [typesOptions, setTypesOptions] = useState([])
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)

  const openManagerModal = () => setIsManagerModalOpen(true)
  const closeManagerModal = () => setIsManagerModalOpen(false)

  const openDelegateModal = () => setIsDelegateModalOpen(true)
  const closeDelegateModal = () => setIsDelegateModalOpen(false)

  const openStoreModal = () => setIsStoreModalOpen(true)
  const closeStoreModal = () => setIsStoreModalOpen(false)

  const openConfirmModal = () => setIsConfirmModalOpen(true)
  const closeConfirmModal = () => setIsConfirmModalOpen(false)

  const { showNotification } = useNotification()

  const {
    data: managersData,
    isLoading: isManagersLoading,
    refetch: refetchManagers
  } = useManagersQuery(
    showNotification,
    '/iam/external-partners/summaries/managers-slim'
  )

  useEffect(() => {
    if (managersData) {
      setManagersOptions(
        managersData.map(manager => ({
          label: manager.fullName,
          value: manager.id
        }))
      )
    }
  }, [managersData])

  const {
    data: delegatesData,
    isLoading: isDelegatesLoading,
    refetch: refetchDelegates
  } = useDelegatesQuery(
    showNotification,
    toTableGETUrl({
      url: '/iam/external-partners/summaries/delegates',
      tableFilters: [{ name: 'isEnabled', value: true }]
    })
  )

  useEffect(() => {
    if (delegatesData) {
      setDelegatesOptions(
        delegatesData.map(delegate => ({
          label: `${delegate.firstName} ${delegate.lastName}`,
          value: delegate.id
        }))
      )
    }
  }, [delegatesData])

  const {
    data: storesData,
    isLoading: isStoresLoading,
    refetch: refetchStores
  } = useStoresQuery(
    showNotification,
    toTableGETUrl({
      url: '/iam/external-partners/summaries/stores',
      tableFilters: [{ name: 'isEnabled', value: true }],
      extraCustomUrlParams: [
        {
          name: 'legalEntityId',
          value: formik.values.legalEntityId ?? 0
        }
      ]
    }),
    { enabled: externalPartnerType?.label === 'Mandatar' }
  )

  useEffect(() => {
    if (storesData) {
      setStoresOptions(
        storesData.map(store => ({
          label: store.storeName ?? '',
          value: store.id
        }))
      )
    }
  }, [storesData])

  const {
    data: countriesData,
    isLoading: isCountriesLoading,
    refetch: refetchCountries
  } = useCountriesQuery(
    showNotification,
    toTableGETUrl({
      page: 0,
      rowsPerPage: 1000,
      url: '/iam/external-partners/countries'
    })
  )

  useEffect(() => {
    if (countriesData) {
      setCountriesOptions(
        countriesData.map(country => ({
          label: country.name,
          value: country.id
        }))
      )
    }
  }, [countriesData])

  const {
    data: typesData,
    isLoading: isTypesLoading,
    refetch: refetchTypes
  } = useLegalEntityTypesQuery(showNotification, '/iam/external-partners/legal-entity-type')

  useEffect(() => {
    if (typesData) {
      setTypesOptions(
        typesData.map(type => ({
          label: type.name,
          value: type.id
        }))
      )
    }
  }, [typesData])

  // TODO implement interfaces
  const legalEntitySelectSearch = useSelectSearch<any, any>({
    url: toTableGETUrl({
      url: '/iam/external-partners/legal-entities',
      tableFilters: [{ name: 'isEnabled', value: true }]
    }),
    searchFields: ['name', 'code'],
    optionsMap: item => ({
      ...item,
      label: item?.name,
      value: item?.id
    }),
    initialSearchText: formik.values.isNewLegalEntity
      ? ''
      : (formik.values?.legalEntityName ?? '')
  })

  return (
    <Box>
      <EditManagerModal
        isModalOpen={isManagerModalOpen}
        onModalClose={closeManagerModal}
        onSuccess={() => refetchManagers()}
        selectManager={manager =>
          formik.setFieldValue('manager', {
            label: `${manager.firstName} ${manager.lastName}`,
            value: manager.id
          })
        }
      />
      <EditDelegateModal
        isModalOpen={isDelegateModalOpen}
        onModalClose={closeDelegateModal}
        onSuccess={() => refetchDelegates()}
        selectDelegate={delegate =>
          formik.setFieldValue('delegate', {
            label: `${delegate.firstName} ${delegate.lastName}`,
            value: delegate.id
          })
        }
      />
      <NewStoreModal
        isModalOpen={isStoreModalOpen}
        closeModal={closeStoreModal}
        onSuccess={() => refetchStores()}
        selectStore={store =>
          formik.setFieldValue('store', [
            ...formik.values.store,
            {
              label: store.name,
              value: store.id
            }
          ])
        }
      />
      <PartnerConfirmationModal
        isModalOpen={isConfirmModalOpen}
        cancelModal={closeConfirmModal}
        partnerFormik={partnerFormik}
        assignmentFormik={formik}
        isSubmitting={isSubmitting}
      />
      <Grid container spacing={2}>
        <Grid item xs={8}>
          <Autocomplete
            {...legalEntitySelectSearch}
            label="Select legal entity"
            variant="outlined"
            freeSolo
            autoComplete={false}
            filterOptions={x => x}
            value={formik.values.existingLegalEntityName}
            onChange={(event, value) => {
              if (value?.name) {
                formik.setFieldValue('legalEntityId', value.id)
                formik.setFieldValue('existingLegalEntityName', value.name)
                formik.setFieldValue('legalEntityName', value.name)
                formik.setFieldValue('legalEntityCIF', value.code)
                formik.setFieldValue('legalEntityCountry', {
                  label: value.country.name,
                  value: value.country.id
                })
              } else {
                formik.setFieldValue('legalEntityId', '')
                formik.setFieldValue('existingLegalEntityName', value)
                formik.setFieldValue('legalEntityName', '')
                formik.setFieldValue('legalEntityCIF', '')
                formik.setFieldValue('legalEntityCountry', '')
              }
            }}
            onClose={event => {
              if ((event.target as HTMLInputElement).tagName.toUpperCase() === 'INPUT') {
                formik.setFieldValue(
                  'existingLegalEntityName',
                  (event.target as HTMLInputElement).value
                )
                formik.setFieldValue('legalEntityName', '')
                formik.setFieldValue('legalEntityId', '')
                formik.setFieldValue('legalEntityCIF', '')
                formik.setFieldValue('legalEntityCountry', '')
                // formik.setFieldValue('legalEntityType', '')
              }
            }}
            error={!!formik.errors.existingLegalEntityName}
            helperText={formik.errors.existingLegalEntityName as string}
            disabled={formik.values.isNewLegalEntity}
          />
        </Grid>
        <Grid item xs={4} style={{ display: 'flex', alignItems: 'center', lineHeight: '56px' }}>
          <Box style={{ position: 'relative', left: '16px', top: '6px' }}>
            <CustomCheckbox
              checked={formik.values.isNewLegalEntity}
              label="New legal entity"
              onChange={() => {
                formik.setFieldValue('isNewLegalEntity', !formik.values.isNewLegalEntity)
                legalEntitySelectSearch.setSearchText('')
                formik.setFieldValue('legalEntityId', null)
                formik.setFieldValue('existingLegalEntityName', null)
                formik.setFieldValue('legalEntityName', '')
                formik.setFieldValue('legalEntityCIF', '')
                formik.setFieldValue('legalEntityCountry', '')
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Input
            type="text"
            variant="outlined"
            name="legalEntityName"
            label="Legal entity name (ANAF registered name for Romanian companies)"
            width="100%"
            value={formik.values.legalEntityName}
            onChange={formik.handleChange}
            required
            disabled={formik.values.legalEntityId || !formik.values.isNewLegalEntity}
            error={!!formik.errors.legalEntityName}
            helperText={formik.errors.legalEntityName as string}
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Input
                type="text"
                variant="outlined"
                name="legalEntityCIF"
                label="Fiscal identification code (CIF)"
                width="100%"
                value={formik.values.legalEntityCIF}
                onChange={formik.handleChange}
                required
                disabled={formik.values.legalEntityId || !formik.values.isNewLegalEntity}
                error={!!formik.errors.legalEntityCIF}
                helperText={formik.errors.legalEntityCIF as string}
              />
            </Grid>
            <Grid item xs={6}>
              <AsyncAutocomplete
                variant="outlined"
                name="legalEntityCountry"
                label="Legal entity country"
                value={formik.values.legalEntityCountry}
                options={countriesOptions}
                onChange={value => formik.setFieldValue('legalEntityCountry', value)}
                required
                disabled={formik.values.legalEntityId || !formik.values.isNewLegalEntity}
                error={!!formik.errors.legalEntityCountry}
                helperText={
                  typeof formik.errors.legalEntityCountry === 'object' &&
                  !Array.isArray(formik.errors.legalEntityCountry)
                    ? (formik.errors.legalEntityCountry?.value as string)
                    : ''
                }
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Input
            type="text"
            variant="outlined"
            name="jobTitle"
            label="Job Title"
            width="100%"
            value={formik.values.jobTitle}
            onChange={formik.handleChange}
            required
            error={!!formik.errors.jobTitle}
            helperText={formik.errors.jobTitle as string}
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <DatePicker
                variant="outlined"
                label="Start Date"
                value={dayjs(formik.values.startDate)}
                onChange={value => formik.setFieldValue('startDate', value)}
                error={!!formik.errors.startDate}
                helperText={formik.errors.startDate as string}
                required
                style={{ width: '100%' }}
                minDate={dayjs()}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePicker
                variant="outlined"
                label="End Date"
                value={dayjs(formik.values.endDate)}
                onChange={value => formik.setFieldValue('endDate', value)}
                error={!!formik.errors.endDate}
                helperText={formik.errors.endDate as string}
                minDate={dayjs(formik.values.startDate)}
                style={{ width: '100%' }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={8}>
          <FormGroup row style={{ padding: 0 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.adAccount}
                  name="adAccount"
                  onChange={formik.handleChange}
                />
              }
              label="AD Account"
              className={sl.digitalAccountCheckboxLabel}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.ldapAccount}
                  name="ldapAccount"
                  onChange={formik.handleChange}
                />
              }
              label="LDAP Account"
              className={sl.digitalAccountCheckboxLabel}
              disabled={isLDAPdisabled}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.googleAccount}
                  name="googleAccount"
                  onChange={event => {
                    const isChecked = (event.target as HTMLInputElement).checked
                    formik.setFieldValue('googleAccount', isChecked)

                    if (isChecked) {
                      setIsLDAPDisabled(true)
                      formik.setFieldValue('ldapAccount', true)
                    } else {
                      setIsLDAPDisabled(false)
                      formik.setFieldValue('ldapAccount', false)
                    }
                  }}
                />
              }
              label="Google Account"
              className={sl.digitalAccountCheckboxLabel}
            />
          </FormGroup>
          {formik.touched.googleAccount && formik.errors.googleAccount ? (
            <FormHelperText error>{formik.errors.googleAccount}</FormHelperText>
          ) : null}
        </Grid>
        <Grid item xs={12} md={4}>
          <Box
            style={{
              fontSize: '10px',
              fontFamily: 'Ubuntu',
              lineHeight: '42px',
              textAlign: 'right'
            }}
          >
            (one digital account mandatory)
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <AsyncAutocomplete
                variant="outlined"
                name="manager"
                label="Manager"
                value={formik.values.manager}
                options={managersOptions}
                onChange={value => {
                  formik.setFieldValue('manager', value)
                  if (value.delegate) {
                    formik.setFieldValue('delegate', value.delegate)
                  } else {
                    formik.setFieldValue('delegate', null)
                  }
                }}
                required
                error={!!formik.errors.manager}
                helperText={formik.errors.manager as string}
              />
            </Grid>
            <Grid item xs={4}>
              <Box style={{ marginTop: '20px', textAlign: 'right' }}>
                <IconButton
                  endIcon={<Person />}
                  variant="outlined"
                  label="New manager"
                  onClick={() => openManagerModal()}
                  style={{ flex: 'none' }}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <AsyncAutocomplete
                variant="outlined"
                name="status"
                label="Delegate"
                value={formik.values.delegate}
                options={delegatesOptions}
                onChange={value => formik.setFieldValue('delegate', value)}
                error={!!formik.errors.delegate}
                helperText={formik.errors.delegate as string}
              />
            </Grid>
            <Grid item xs={4}>
              <Box style={{ marginTop: '20px', textAlign: 'right' }}>
                <IconButton
                  endIcon={<GroupsOutlined />}
                  variant="outlined"
                  label="New delegate"
                  onClick={() => openDelegateModal()}
                  style={{ flex: 'none' }}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
        {externalPartnerType?.label === 'Mandatar' && (
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <AsyncAutocomplete
                  variant="outlined"
                  name="store"
                  label="Store"
                  value={formik.values.store}
                  options={storesOptions}
                  multiple
                  required
                  onChange={value => formik.setFieldValue('store', value)}
                  error={!!formik.errors.store}
                  helperText={formik.errors.store as string}
                />
              </Grid>
              <Grid item xs={4}>
                <Box style={{ marginTop: '20px', textAlign: 'right' }}>
                  <IconButton
                    endIcon={<AddShoppingCart />}
                    variant="outlined"
                    label="Assign new store"
                    onClick={() => openStoreModal()}
                    style={{ flex: 'none' }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid item xs={12}>
          <Input
            type="textarea"
            name="assignmentDetails"
            label="Additional details"
            style={{ width: '100%' }}
            rows={3}
            variant="outlined"
            value={formik.values.assignmentDetails}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              formik.setFieldValue('assignmentDetails', event.currentTarget.value)
            }
            error={!!formik.errors.assignmentDetails}
            helperText={formik.errors.assignmentDetails as string}
          />
        </Grid>
        <Grid item xs={12} className={s.modalButtons}>
          <IconButton
            startIcon={<KeyboardArrowLeft />}
            variant="outlined"
            label="Partner details"
            onClick={onBack}
            style={{ flex: 'none' }}
          />
          <Button
            variant="contained"
            label="Save"
            onClick={() => {
              formik.validateForm().then(errors => {
                if (Object.keys(errors).length === 0) {
                  openConfirmModal()
                } else {
                  formik.setTouched(
                    Object.keys(formik.values).reduce((acc, key) => {
                      acc[key] = true
                      return acc
                    }, {})
                  )
                }
              })
            }}
          />
        </Grid>
      </Grid>
    </Box>
  )
}

export default EditAssignmentStep
